import React, { useEffect, useContext, useState, useRef } from "react";
import CredentialTemplateCard from "../../components/Cards/CredentialTemplateCard/CredentialTemplateCard";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";
import AuthContext from "../../store/auth-context";
import { useNavigate } from "react-router-dom";
import CalculateCardsPerRow from "../../service/CalculateCardsPerRow";
import useSearchDebounce from "../../hooks/useSearchDebounce";

function Discovery() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const [cardWidth] = useState("19.125rem");
  const [gap] = useState("1.5rem");
  const containerRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(0);

  const [isSearching, setIsSearching] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [respContentList, setRespContentList] = useState([]);
  const [respCategoryList, setRespCategoryList] = useState([]);
  const [contextList, setContextList] = useState([]);
  const [defaultCategory, setDefaultCategory] = useState([]);
  const [count, setCount] = useState(0);
  const [countSearch, setCountSearch] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setlimit] = useState(8);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const [isLodingCredTemplates, setIsLoadingCredTemplates] = useState(false);

  // Function to handle template card click
  const handleTemplateClick = (credential_template_id) => {
    if (authContext.isLoggedIn) {
      // User is logged in, navigate to the edit page
      navigate(`/certificate/edit/${credential_template_id}`);
    } else {
      // User is logged out, open login modal
      authContext.setTriggerLoginModuleToggleValue(true);
      // Store the target URL to redirect after login
      sessionStorage.setItem(
        "redirectAfterLogin",
        `/certificate/edit/${credential_template_id}`
      );
    }
  };

  const GET_TEMPLATE_LIST = async (
    limit,
    offset,
    cardsPerRow,
    isLoadMore = false
  ) => {
    if (isLoadMore) {
      setIsLoadingCredTemplates(true);
    }

    try {
      const response = await GET(
        `${EntityConstants.DISCOVERY_TEMPLATE}limit=${limit}&offset=${offset}&cred_per_category=${cardsPerRow}`
      );
      const data = await response.json();

      setCount(data.count);
      setOffset(offset);

      if (isLoadMore) {
        setRespContentList((prev) => [...prev, ...data.results]);
        setRespCategoryList((prev) => [...prev, ...data.results]);
      } else {
        setRespContentList(data.results);
        setRespCategoryList(data.results);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingCredTemplates(false);
    }
  };

  const GET_DEFAULT_CATEGORY = async (cardsPerRow) => {
    await GET(
      `${EntityConstants.DISCOVERY_DEFAULT_CATEGORY}?limit=${cardsPerRow}&offset=0`
    )
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        setDefaultCategory(data.results);
      });
  };

  const performSearchAPI = async (searchTerm) => {
    try {
      const response = await GET(
        `${EntityConstants.DISCOVERY_SEARCH}${searchTerm}`
      );
      const data = await response.json();

      const credentials = data.credentials || [];
      const categories = data.categories || [];
      setCountSearch(categories?.length || 0);
      setContextList(credentials);
      setDefaultCategory(categories);
      setIsSearched(true);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.log("Error is : ", error);
        setContextList([]);
        setCountSearch(0);
        setIsSearched(true);
      }
    }
  };

  const { handleSearch } = useSearchDebounce(performSearchAPI, 300);

  useEffect(() => {
    const updateCardsPerRow = () => {
      if (containerRef.current) {
        const calculatedCardsPerRow = CalculateCardsPerRow(
          containerRef,
          cardWidth,
          gap
        );
        const finalCardsPerRow =
          calculatedCardsPerRow < 4 ? 4 : calculatedCardsPerRow;
        setCardsPerRow(finalCardsPerRow);
      }
    };

    updateCardsPerRow();
    window.addEventListener("resize", updateCardsPerRow);
    return () => window.removeEventListener("resize", updateCardsPerRow);
  }, [cardWidth, gap]);

  useEffect(() => {
    if (respContentList) {
      setContextList([...respContentList]);
    }
  }, [respContentList]);

  useEffect(() => {
    if (respCategoryList) {
      setDefaultCategory([...respCategoryList]);
    }
  }, [respCategoryList]);

  useEffect(() => {
    if (
      authContext.hasDiscoveryReachedBottom &&
      !isSearched &&
      !isLodingCredTemplates &&
      contextList.length < count &&
      cardsPerRow > 0 &&
      contextList.length > 0
    ) {
      const nextOffset = offset + limit;
      GET_TEMPLATE_LIST(limit, nextOffset, cardsPerRow, true);
    }
  }, [
    authContext.hasDiscoveryReachedBottom,
    isSearched,
    isLodingCredTemplates,
    contextList.length,
    count,
    cardsPerRow,
    offset,
    limit,
  ]);

  useEffect(() => {
    if (cardsPerRow > 0) {
      const initialLimit = cardsPerRow * 2; // 2 full rows
      setlimit(initialLimit);
      setOffset(0);
      GET_DEFAULT_CATEGORY(cardsPerRow);
      GET_TEMPLATE_LIST(initialLimit, 0, cardsPerRow, false);
    }
    sessionStorage.setItem("workflow_id", null);
  }, [cardsPerRow]);

  const curretSearch = (e) => {
    const searchTerm = e.target.value ? e.target.value.trim() : "";

    if (searchTerm.length > 2) {
      setIsSearching(true);
      handleSearch(searchTerm, {
        onStart: () => setIsSearching(true),
        onComplete: () => setIsSearching(false),
        onError: () => {
          setContextList([]);
          setCountSearch(0);
          setIsSearching(false);
        },
        minChars: 3,
      });
    } else {
      setContextList([...respContentList]);
      setDefaultCategory([...respCategoryList]);
      setIsSearched(false);
      setIsSearching(false);
      setCountSearch(0);
    }
  };

  return (
    <div>
      <div className="h-full ">
        <div className="bxm font-CfPrimaryFont bg-cfSecondary min-h-screen">
          <div className="flex flex-col items-center w-full mb-4 ">
            <div
              className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4`}
            >
              <div
                className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
              >
                <p className="text-cfHeadingText font-semibold self-start my-auto">
                  Choose a template
                </p>
                {/* Search */}
                <form
                  onSubmit={(e) => e.preventDefault()}
                  className=" bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] self-center ml-[0rem] md:ml-[0.630rem] md:self-end md:m-0"
                >
                  <input
                    type="text"
                    name="search"
                    id="search"
                    onChange={(e) => curretSearch(e)}
                    placeholder="Search for a certificate or category"
                    className="outline-none border-none p-0 placeholder:text-sm w-full"
                    style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                  />
                </form>
              </div>
            </div>

            <div className="flex flex-col gap-y-4 p-4 w-full">
              <div className="flex flex-col sm:flex-row justify-between w-full">
                <div className="w-full">
                  <div className="flex justify-between mb-[1.063rem] flex-wrap">
                    <div className="flex items-center gap-2">
                      <p className="text-cfHeadingText font-semibold text-lg">
                        Categories
                      </p>
                      {!isSearched && !isSearching && (
                        <p className="text-cfTotalCertificates">({count})</p>
                      )}
                      {isSearched && (
                        <p className="text-cfTotalCertificates">
                          ({countSearch})
                        </p>
                      )}
                    </div>
                    {!showAllCategories && (
                      <div
                        onClick={() => {
                          setShowAllCategories(true);
                          navigate("/categories");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setShowAllCategories(true);
                            navigate("/categories");
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        className="font-cfPrimaryFont text-sm font-semibold text-cfViewAllTagText cursor-pointer"
                        aria-label="View all categories"
                      >
                        <p>View all categories</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center w-full gap-6 flex-wrap">
                    {defaultCategory.length > 0 &&
                      (showAllCategories
                        ? defaultCategory
                        : defaultCategory.slice(0, 4)
                      ).map(
                        ({
                          category_name,
                          category_description,
                          thumbnail_images,
                          category_id,
                        }) => (
                          <div
                            key={category_id}
                            className={`w-[19.125rem] h-[5.563rem] flex rounded-lg overflow-hidden border-[1.5px] border-cfCardBorder shadow-sm shadow-cfCategoryCardShadow/50 ${
                              category_id
                                ? "cursor-pointer"
                                : "cursor-not-allowed"
                            }`}
                            onClick={() =>
                              category_id &&
                              navigate(
                                `/certificates/${category_name.replace(
                                  / /g,
                                  "+"
                                )}/${category_id}`
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                category_id &&
                                  navigate(
                                    `/certificates/${category_name.replace(
                                      / /g,
                                      "+"
                                    )}/${category_id}`
                                  );
                              }
                            }}
                            tabIndex={category_id ? 0 : -1}
                            role="button"
                            aria-label={`View ${category_name} category`}
                          >
                            <div
                              className={`min-w-[5.625rem] w-[5.625rem] px-3 py-3 bg-cfSearchContainer`}
                            >
                              <img
                                className={`w-full h-full object-cover object-top`}
                                src={
                                  thumbnail_images?.length > 0 &&
                                  thumbnail_images[0]
                                }
                                onError={(e) => {
                                  e.target.src =
                                    thumbnail_images?.length > 0 &&
                                    thumbnail_images[0];
                                }}
                                alt="certificate"
                              />
                            </div>
                            <div className="flex flex-col px-[0.438rem] my-auto">
                              <p className="text-cfCategoryCardHeadingText font-semibold text-sm line-clamp-1">
                                {category_name}
                              </p>
                              <p className={`text-xs line-clamp-2`}>
                                {category_description}
                              </p>
                            </div>
                          </div>
                        )
                      )}
                  </div>
                </div>
              </div>
              <div ref={containerRef} className="w-full">
                {isSearched && (
                  <div className="text-cfHeadingText font-semibold text-lg ">
                    <p>Searched certificates</p>
                  </div>
                )}
                {isSearching || isSearched ? (
                  <div className="flex items-center gap-6 flex-wrap w-full py-[0.6rem]">
                    {contextList.length > 0 &&
                      contextList.map(
                        (
                          {
                            name,
                            description,
                            credential_template_id,
                            tags,
                            thumbnail_svg_url,
                          },
                          index
                        ) => (
                          <CredentialTemplateCard
                            key={index}
                            name={name}
                            description={description}
                            credential_template_id={credential_template_id}
                            tagsList={tags}
                            thumbnail_image={thumbnail_svg_url}
                            onCardClick={() =>
                              handleTemplateClick(credential_template_id)
                            }
                          />
                        )
                      )}
                    {isSearched && contextList.length === 0 && (
                      <p className="text-grey-50 font-semibold text-lg mt-9">
                        No results found
                      </p>
                    )}
                  </div>
                ) : (
                  contextList.length !== 0 &&
                  contextList.map(
                    ({
                      category_id,
                      category_name,
                      credentials_count,
                      credentials,
                    }) =>
                      credentials_count > 0 && (
                        <div
                          className="flex flex-col gap-3 pb-6 last:pb-0"
                          key={category_name}
                        >
                          <div className="flex flex-col sm:grid grid-cols-auto-fit-minmax justify-between w-full gap-y-3">
                            <div className="flex items-center gap-2">
                              <p className="text-cfHeadingText font-semibold text-lg">
                                {category_name === "Custom Templates"
                                  ? "My Designs"
                                  : category_name}
                              </p>
                              <p className="text-cfTotalCertificates">{`(${credentials_count})`}</p>
                            </div>
                            {!(credentials.length >= credentials_count) && (
                              <div
                                className={`font-cfPrimaryFont text-sm font-semibold text-cfViewAllTagText text-right cursor-pointer`}
                                onClick={() =>
                                  category_id &&
                                  navigate(
                                    `/certificates/${category_name.replace(
                                      / /g,
                                      "+"
                                    )}/${category_id}`
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    category_id &&
                                      navigate(
                                        `/certificates/${category_name.replace(
                                          / /g,
                                          "+"
                                        )}/${category_id}`
                                      );
                                  }
                                }}
                                tabIndex={category_id ? 0 : -1}
                                role="button"
                                aria-label={`View all ${category_name} certificates`}
                              >
                                <p>View all</p>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-6 flex-wrap w-full">
                            {Array.isArray(credentials) &&
                              credentials.map(
                                (
                                  {
                                    name,
                                    description,
                                    credential_template_id,
                                    tags,
                                    thumbnail_svg_url,
                                  },
                                  index
                                ) => {
                                  return (
                                    <>
                                      <CredentialTemplateCard
                                        key={index}
                                        name={name}
                                        description={description}
                                        credential_template_id={
                                          credential_template_id
                                        }
                                        tagsList={tags}
                                        thumbnail_image={thumbnail_svg_url}
                                        onCardClick={() =>
                                          handleTemplateClick(
                                            credential_template_id
                                          )
                                        }
                                      />
                                    </>
                                  );
                                }
                              )}
                          </div>
                        </div>
                      )
                  )
                )}
              </div>
              <div>{isLodingCredTemplates && <CredentialTemplateCard />}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Discovery;
